// Library
import React from 'react'
import * as yup from 'yup'
import { useHistory, useParams } from 'react-router-dom'

// Use
import useForm from 'use/form'
import { useSelector, useDispatch } from 'react-redux'

// Services
import dialog from 'services/dialog'
import loadingService from 'services/loading'

// Helper
import { resolveErrorMessage } from 'helpers'

// Routes
import { PATH_FORGOT_PASSWORD_SUCCESS, PATH_LOGIN } from 'routes/paths'

// Ui
import Row from 'ui/Row'
import Col from 'ui/Col'
import Button from 'ui/Button'
import ShowInputPassword from 'ui/Form/ShowInputPassword'
// Icons
import IconEmail from 'ui/Icons/IconEmail'

// Component
import PopupSecurityCode from 'components/Lessor/PopupSecurityCode'
import PopupForgotPassword from 'components/Lessor/PopupForgotPassword'
import PopupOTP from 'components/Auth/PopupOTP'

// Api
import { changePassword, validatePassword } from 'api/profile'
import { logout } from 'api/auth'
import InputPassword from 'ui/Form/InputPassword'
import Text from 'ui/Text'


// Validation schema
const validationSchema = yup.object().shape({
  currentPassword: yup
    .string()
    .trim()
    .required('The password field is required'),
  newPassword: yup
    .string()
    .matches(
      `^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{9,}$`,
      () => <div />
    )
    .notOneOf([yup.ref('currentPassword'), null],'New password must be different from old password')
    .required('The password field is required'),
  newPasswordConfirm: yup
    .string()
    .oneOf([yup.ref('newPassword'), null], `The password confirm don't match`)
    .required('The confirm password field is required'),
})

// InitialForm
const initialForm = {
  currentPassword: '',
  newPassword: '',
  newPasswordConfirm: '',
}

const LessorChangePasswordForm = () => {
  // State
  const history = useHistory()
  const dispatch = useDispatch()
  const { companyId, vendorId } = useParams()
  const user = useSelector((state) => state.auth.user)

  // UseFormHook
  const { form, errors, validate, onChange, onBlur, setFieldError } = useForm({
    schema: validationSchema,
    initialForm,
  })

  // Event
  const onSubmitForm = async (event) => {
    event.preventDefault()
    try {
      await validate()
    } catch (error) {
      return
    }
    try {
      await validatePassword({
        email: user.email,
        data: form,
        companyId,
        vendorId,
      })
    } catch (error) {
      setFieldError('currentPassword', await resolveErrorMessage(error))
      return
    }

    dialog.create(PopupSecurityCode, {
      onConfirm: () => onShowOtpVerify(),
    })
  }

  const onShowOtpVerify = () => {
    dialog.create(PopupOTP, {
      title: 'Enter OTP',
      icon: <IconEmail />,
      description: `Enter one-time PIN sent to your email\n${user.email}`,
      onConfirm: async ({ setLoading, setVisible }) => {
        try {
          setLoading(true)
          // Api change password
          await changePassword({
            email: user.email,
            data: form,
            companyId,
            vendorId,
          })
          await onLogout()
          onChangePasswordSuccess()
        } catch (error) {
          setFieldError('currentPassword', await resolveErrorMessage(error))
        } finally {
          setLoading(false)
          setVisible(false)
        }
      },
    })
  }

  const onChangePasswordSuccess = () => {
    dialog.alert({
      title: 'New password saved',
      message: `Thank you for keeping your account secure.\n
          Make sure to not share your password with anyone else`,
      onConfirm: ({ setVisible }) => {
        setVisible(false)
        history.replace(PATH_LOGIN)
      },
    })
  }

  const onLogout = async () => {
    try {
      loadingService.show()
      await dispatch(logout())
    } catch (error) {
      //
    } finally {
      loadingService.hide()
    }
  }

  const onGotoForgotPassword = async () => {
    try {
      dialog.create(PopupForgotPassword, {
        onConfirm: async() => {
          await onLogout()
          history.replace(PATH_FORGOT_PASSWORD_SUCCESS)
        },
      })
      
    } catch (error) {
      setFieldError('forgotPassword', await resolveErrorMessage(error))
    }
  }

  //Render
  return (
    <form id="changePasswordForm" onSubmit={onSubmitForm}>
      <Row>
        <Col sm={8} md={5}>
          <ShowInputPassword
            name="currentPassword"
            label="Current"
            placeholder="Enter current password"
            value={form.currentPassword}
            errors={errors}
            onChange={onChange}
            onBlur={onBlur}
          />
        </Col>
      </Row>
      <Row>
        <Col sm={8} md={5}>
          <Text
            size={ 14}
            paddingTop={15}
            paddingBottom={0}
            color="#000000"
            bold
          >
            New Password must be a combination of:
          </Text>
          <InputPassword
            name="newPassword"
            label="New Password"
            placeholder="Enter new password"
            value={form.newPassword}
            errors={errors}
            onChange={onChange}
            onBlur={onBlur}
            paddingTop={0}
          />
        </Col>
      </Row>
      <Row>
        <Col sm={8} md={5}>
          <ShowInputPassword
            name="newPasswordConfirm"
            label="Re-enter New Password"
            placeholder="Re-enter new password"
            value={form.newPasswordConfirm}
            errors={errors}
            onChange={onChange}
            onBlur={onBlur}
          />
        </Col>
      </Row>
      <Row>
        <Col sm={8} md={5}>
          <Button variant="link" onClick={onGotoForgotPassword} underline>
            Forgot current password?
          </Button>
        </Col>
      </Row>
    </form>
  )
}

export default LessorChangePasswordForm
